import { router } from "@inertiajs/vue3"

type MetaEventName =
  | "PageView"
  | "ViewContent"
  | "AddToCart"
  | "InitiateCheckout"
  | "Purchase"
  | "Lead"

type MetaCustomData = {
  content_type?: string
  content_ids?: string[]
  contents?: Array<{ id: string; quantity: number; item_price: number }>
  num_items?: number
  value?: number
  currency?: string
  order_id?: string | null
  lead_type?: string
}

type MetaWindow = Window & {
  fbq?: (...args: unknown[]) => void
  __metaPixelReady?: boolean
}

type PendingMetaEvent = {
  pageUrl: string
  send: () => void
}

const metaWindow = (): MetaWindow | null =>
  typeof window === "undefined" ? null : (window as MetaWindow)

const currentPageUrl = (): string => metaWindow()?.location?.href ?? ""

let pendingEvents: PendingMetaEvent[] = []
let isListeningForPixel = false

const isMetaPixelReady = (): boolean => {
  const w = metaWindow()

  return Boolean(w?.__metaPixelReady && typeof w.fbq === "function")
}

const flushPendingEvents = (): void => {
  const events = pendingEvents
  pendingEvents = []
  events.forEach((event) => event.send())
}

const listenForPixel = (): void => {
  const w = metaWindow()

  if (!w || isListeningForPixel) {
    return
  }

  isListeningForPixel = true
  w.addEventListener("meta:pixel-ready", flushPendingEvents)
}

const createMetaEventId = (prefix: string): string => {
  const randomId =
    metaWindow()?.crypto?.randomUUID?.() ??
    `${Date.now()}-${Math.random().toString(36).slice(2)}`

  return `${prefix}.${randomId}`
}

const trackMetaEvent = (
  eventName: MetaEventName,
  customData: MetaCustomData = {},
  eventId: string = createMetaEventId(eventName.toLowerCase()),
  onSent?: () => void
): void => {
  if (!metaWindow()) {
    return
  }

  const send = (): void => {
    metaWindow()?.fbq?.("track", eventName, customData, { eventID: eventId })
    onSent?.()
  }

  if (isMetaPixelReady()) {
    send()

    return
  }

  listenForPixel()
  pendingEvents.push({ pageUrl: currentPageUrl(), send })
}

const trackMetaPageViews = (): void => {
  if (!metaWindow()) {
    return
  }

  router.on("navigate", () => {
    pendingEvents = pendingEvents.filter(
      (event) => event.pageUrl === currentPageUrl()
    )
    trackMetaEvent("PageView")
  })
}

export type { MetaCustomData, MetaEventName }

export { createMetaEventId, trackMetaEvent, trackMetaPageViews }
